import { Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError } from '@angular/material/form-field';
import { WizardStore } from '@program-suite/data-common';
import { ButtonComponent } from '../../button/button.component';
import { ModalLayoutComponent } from '../modal-layout/modal-layout.component';
import { PayrollClassesComponent } from '../../payroll-classes/payroll-classes.component';

export interface MissingInfoModalData {
  // The quote wizard's step 2 form, so answers here fill in step 2 directly
  form: FormGroup;
  // Same rule as step 2's "Proceed to Price" button
  disabled: Signal<boolean>;
  // Defaults to the quote wizard's wording; coverage adjustment talks about the policy instead
  title?: string;
  message?: string;
  buttonText?: string;
  // Escape hatch when the agent doesn't have the answers yet, e.g. "Return to Submission" or "Return to Policy"
  returnText: string;
}

// Shown when an indication or quote was saved before the newer Step 2 questions existed,
// so the agent answers them before making changes.
@Component({
  selector: 'lib-missing-info-modal',
  imports: [ModalLayoutComponent, ButtonComponent, PayrollClassesComponent, MatError],
  templateUrl: './missing-info-modal.component.html',
  styleUrl: './missing-info-modal.component.scss',
})
export class MissingInfoModalComponent {
  data = inject(MAT_DIALOG_DATA) as MissingInfoModalData;
  dialogRef = inject(MatDialogRef<MissingInfoModalComponent>);
  // The opener's store, passed in through the modal's injector
  wizardStore = inject(WizardStore);

  // Only the questions shown here, so an unrelated step 2 field can't keep the button disabled
  private readonly controls = ['numberOfFieldOwners', 'numberOfEmployees', 'employeePayroll', 'classes'];
  // Re-check on every edit; validity is already updated when valueChanges emits
  private value = toSignal(this.data.form.valueChanges, { initialValue: this.data.form.value });
  disabled = computed(() => {
    this.value();
    return this.data.disabled() || this.controls.some((name) => this.data.form.get(name)?.invalid);
  });

  title = this.data.title ?? 'We’ve Updated the Rater!';
  message =
    this.data.message ??
    'Great news! The RCIP rater has been updated since this risk was rated. Please provide the following info so we can provide an updated quote. Thank you.';
  buttonText = this.data.buttonText ?? 'Update Quote';

  updateQuote() {
    this.dialogRef.close(true);
  }

  // Closes with false so the opener sends them back instead of re-rating
  returnLater() {
    this.dialogRef.close(false);
  }
}
